import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import API_BASE_URL from '../config/api-config';


const FacebookPageSelector = () => {
  const location = useLocation();
  const navigate = useNavigate();

  //Environment Variables
  const FACEBOOK_APP_ID = "1245931220393560";
  
  // State management
  const [pages, setPages] = useState([]);
  const [selectedFacebookPage, setSelectedFacebookPage] = useState(null);
  const [selectedInstagramAccount, setSelectedInstagramAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Extract parameters from URL
  const urlParams = new URLSearchParams(location.search);
  const userId = urlParams.get('userId');
  const feedId = urlParams.get('feedId'); // NEW: Feed-level support
  const feedName = urlParams.get('feedName'); // Optional: for better UX
  
  // Determine if this is feed-level or account-level selection
  const isFeedLevel = !!feedId;
  
  console.log("🔍 FacebookPageSelector context:", { 
    userId, 
    feedId: feedId || "none (account-level)", 
    feedName,
    isFeedLevel 
  });

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Facebook pages on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        if (!idToken) {
          throw new Error('Authentication token not found');
        }

        console.log('📡 Fetching Facebook pages...');
        const queryParams = new URLSearchParams({ userId });
        if (feedId) {
          queryParams.append('feedId', feedId);
        }

        const response = await fetch(`${API_BASE_URL}/facebook/select-page?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Authorization': idToken,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('No Facebook connection found. Please connect your Facebook account first in Account Settings.');
          }
          throw new Error(`Failed to fetch pages: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Facebook pages received:', data);
        
        if (!data.pages || data.pages.length === 0) {
          setError('No Facebook pages found. Please create a Facebook page first.');
        }
        
        setPages(data.pages);
        
      } catch (err) {
        console.error('❌ Error fetching Facebook pages:', err);
        setError(err.message || 'Failed to fetch Facebook pages');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [location, userId, feedId]);

  // Handler for Facebook page selection
  const handleFacebookPageSelect = (page) => {
    setSelectedFacebookPage(page);
  };

  // Handler for Instagram account selection
  const handleInstagramAccountSelect = (instagramData) => {
    setSelectedInstagramAccount(instagramData);
  };

  // Submit selections - now supports both feed-level and account-level
  const handleConnect = async () => {
    if (!selectedFacebookPage && !selectedInstagramAccount) {
      setError('Please select at least one account to connect');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('Authentication token not found');
      }

      console.log('🔄 Submitting account selections...', { 
        isFeedLevel, 
        feedId,
        selectedFacebookPage: selectedFacebookPage?.id,
        selectedInstagramAccount: selectedInstagramAccount?.id 
      });
      
      // Prepare request payload
      const payload = { 
        userId,
        selectedPageId: selectedFacebookPage?.id || null,
        connectInstagram: !!selectedInstagramAccount,
        instagramAccountId: selectedInstagramAccount?.id || null
      };
      
      // NEW: Add feedId for feed-level connections
      if (isFeedLevel) {
        payload.feedId = feedId;
      }
      
      // Use existing endpoint with enhanced payload
      const response = await fetch(`${API_BASE_URL}/facebook/select-page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to connect accounts (${response.status}): ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Account connection successful:', result);
      
      // NEW: Different redirect logic for feed-level vs account-level
      if (isFeedLevel) {
        // Redirect back to feed settings with success parameters
        const params = new URLSearchParams();
        params.append('feed_connection_success', 'true');
        
        if (selectedFacebookPage) {
          params.append('facebook_connected', 'true');
          params.append('page_name', selectedFacebookPage.name);
        }
        
        if (selectedInstagramAccount) {
          params.append('instagram_connected', 'true');
          params.append('instagram_username', selectedInstagramAccount.username);
        }
        
        // Navigate back to the podcast feed settings page
        navigate(`/podcast/${feedId}/settings?feedId=${params.toString()}`);
      } else {
        // Original account-level redirect
        const params = new URLSearchParams();
        
        if (selectedFacebookPage) {
          params.append('facebook_connected', 'true');
          params.append('page_name', selectedFacebookPage.name);
        }
        
        if (selectedInstagramAccount) {
          params.append('instagram_connected', 'true');
          params.append('instagram_username', selectedInstagramAccount.username);
        }
        
        navigate(`/account-settings?${params.toString()}`);
      }
      
    } catch (err) {
      console.error('❌ Error connecting accounts:', err);
      setError(err.message || 'Failed to connect accounts');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isFeedLevel) {
      // Navigate back to feed settings
      navigate(`/podcast-feed-settings?feedId=${feedId}&facebook_cancelled=true`);
    } else {
      // Original account-level redirect
      navigate('/account-settings?facebook_cancelled=true');
    }
  };

  const handleCheckConnectionSettings = async () => {
    try {
      const session = await fetchAuthSession();
      const currentUserId = session.tokens?.idToken?.payload?.sub;

      if (!currentUserId) {
        alert('Please log in first.');
        return;
      }

      // Store feed context for after OAuth completion
      sessionStorage.setItem("oauth_provider", "facebook");
      sessionStorage.setItem("oauth_feedId", feedId);
      sessionStorage.setItem("oauth_feedName", feedName);
      sessionStorage.setItem("oauth_action", "full_auth_then_page_selection");

      // Redirect to Facebook OAuth with all required scopes
      const redirectUri = encodeURIComponent(`${API_BASE_URL}/facebook/auth`);
      const essentialScopes = [
        'email',
        'public_profile',
        'pages_show_list', 
        'pages_manage_posts'
      ];
      const optionalScopes = [
        'instagram_basic',
        'instagram_content_publish',
        'business_management'
      ];
      const allScopes = [...essentialScopes, ...optionalScopes].join(',');

      // Include userId in state parameter for OAuth callback
      const stateParam = encodeURIComponent(JSON.stringify({ 
        userId: currentUserId, 
        feedId, 
        action: 'full_auth_then_page_selection' 
      }));

      const facebookOAuthUrl = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${redirectUri}&state=${stateParam}&scope=${allScopes}&response_type=code&auth_type=rerequest`;

      console.log("🔄 Redirecting to Facebook OAuth to refresh connection settings...");
      window.location.href = facebookOAuthUrl;

    } catch (error) {
      console.error("❌ Error checking connection settings:", error);
      alert('Failed to check connection settings. Please try again.');
    }
  };

  // Extract Instagram accounts from pages
  const instagramAccounts = pages
    .filter(page => page.has_instagram && page.instagram_business_account)
    .map(page => ({
      id: page.instagram_business_account.id,
      username: page.instagram_username,
      name: page.instagram_name,
      followers: page.instagram_followers,
      profile_picture: page.instagram_profile_picture,
      page_id: page.id // Keep for backend reference
    }));

  if (loading) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
          padding: '32px', 
          textAlign: 'center', 
          border: '1px solid #e5e7eb' 
        }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            marginBottom: '16px', 
            color: '#111827' 
          }}>
            Loading Facebook Pages...
          </h2>
          <p style={{ color: '#6b7280' }}>
            Please wait while we fetch your Facebook pages.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
          padding: '32px', 
          textAlign: 'center', 
          border: '1px solid #e5e7eb' 
        }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            marginBottom: '16px', 
            color: '#dc2626' 
          }}>
            Error Loading Pages
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            {error}
          </p>
          <button 
            onClick={handleCancel}
            style={{
              padding: '8px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {isFeedLevel ? 'Back to Feed Settings' : 'Back to Account Settings'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px' }}>
      {/* Header - Updated for feed context */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        padding: '32px', 
        marginBottom: '24px', 
        border: '1px solid #e5e7eb' 
      }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: 'bold', 
          marginBottom: '8px', 
          color: '#111827' 
        }}>
          {isFeedLevel 
            ? `Select Facebook & Instagram for ${feedName || 'Your Feed'}`
            : 'Select Your Facebook Page & Instagram Account'
          }
        </h1>
        <p style={{ 
          color: '#6b7280', 
          fontSize: '16px' 
        }}>
          {isFeedLevel 
            ? `Choose which Facebook page and Instagram account to use for posting content from this podcast feed.`
            : `Choose which Facebook page and Instagram account to connect to your account.`
          }
        </p>
      </div>

      {/* Selection Interface */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        border: '1px solid #e5e7eb' 
      }}>
        <div style={{ padding: '32px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: windowWidth >= 1024 ? '1fr 1fr' : '1fr', 
            gap: '32px' 
          }}>
            {/* Facebook Pages Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ 
                background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)', 
                color: 'white', 
                padding: '16px', 
                borderRadius: '8px' 
              }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0 0 4px 0' 
                }}>
                  <span style={{ fontSize: '24px', marginRight: '8px' }}>📘</span>
                  Facebook Pages
                </h3>
                <p style={{ 
                  color: '#bfdbfe', 
                  fontSize: '14px', 
                  margin: '0' 
                }}>
                  Choose one Facebook page
                </p>
              </div>
              
              {/* Facebook Page Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pages.map(page => {
                  const isSelected = selectedFacebookPage?.id === page.id;
                  
                  return (
                    <div
                      key={page.id}
                      onClick={() => handleFacebookPageSelect(page)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        padding: '16px',
                        border: isSelected ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                        backgroundColor: isSelected ? '#eff6ff' : 'white',
                        boxShadow: isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#93c5fd';
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <h4 style={{ 
                            fontWeight: '500', 
                            color: '#111827', 
                            marginBottom: '4px',
                            fontSize: '16px'
                          }}>
                            {page.name}
                          </h4>
                          <p style={{ 
                            color: '#6b7280', 
                            fontSize: '14px', 
                            margin: '0 0 4px 0' 
                          }}>
                            {page.category} • {page.fan_count?.toLocaleString() || '0'} followers
                          </p>
                          {page.has_instagram && (
                            <div style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              backgroundColor: '#dcfce7', 
                              color: '#166534', 
                              padding: '2px 8px', 
                              borderRadius: '12px', 
                              fontSize: '12px', 
                              fontWeight: '500' 
                            }}>
                              📱 Instagram Connected
                            </div>
                          )}
                        </div>
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '50%', 
                          border: '2px solid', 
                          borderColor: isSelected ? '#3b82f6' : '#d1d5db', 
                          backgroundColor: isSelected ? '#3b82f6' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {isSelected && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: 'white',
                              borderRadius: '50%'
                            }} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Instagram Accounts Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ 
                background: 'linear-gradient(90deg, #9333ea 0%, #7c3aed 100%)', 
                color: 'white', 
                padding: '16px', 
                borderRadius: '8px' 
              }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0 0 4px 0' 
                }}>
                  <span style={{ fontSize: '24px', marginRight: '8px' }}>📱</span>
                  Instagram Accounts
                </h3>
                <p style={{ 
                  color: '#e9d5ff', 
                  fontSize: '14px', 
                  margin: '0' 
                }}>
                  Choose one Instagram account (optional)
                </p>
              </div>
              
              {/* Instagram Account Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {instagramAccounts.length > 0 ? (
                  instagramAccounts.map(instagram => {
                    const isSelected = selectedInstagramAccount?.id === instagram.id;
                    
                    return (
                      <div
                        key={instagram.id}
                        onClick={() => handleInstagramAccountSelect(instagram)}
                        style={{
                          cursor: 'pointer',
                          borderRadius: '8px',
                          padding: '16px',
                          border: isSelected ? '2px solid #9333ea' : '2px solid #e5e7eb',
                          backgroundColor: isSelected ? '#faf5ff' : 'white',
                          boxShadow: isSelected ? '0 0 0 3px rgba(147, 51, 234, 0.1)' : '0 1px 3px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#c4b5fd';
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {instagram.profile_picture && (
                              <img 
                                src={instagram.profile_picture} 
                                alt={instagram.username}
                                style={{ 
                                  width: '48px', 
                                  height: '48px', 
                                  borderRadius: '50%', 
                                  objectFit: 'cover' 
                                }} 
                              />
                            )}
                            <div>
                              <h4 style={{ 
                                fontWeight: '500', 
                                color: '#111827', 
                                marginBottom: '4px',
                                fontSize: '16px'
                              }}>
                                @{instagram.username}
                              </h4>
                              {instagram.name && (
                                <p style={{ 
                                  color: '#6b7280', 
                                  fontSize: '14px', 
                                  margin: '0 0 4px 0' 
                                }}>
                                  {instagram.name}
                                </p>
                              )}
                              <p style={{ 
                                color: '#6b7280', 
                                fontSize: '12px', 
                                margin: '0' 
                              }}>
                                {instagram.followers?.toLocaleString() || '0'} followers
                              </p>
                            </div>
                          </div>
                          <div style={{ 
                            width: '20px', 
                            height: '20px', 
                            borderRadius: '50%', 
                            border: '2px solid', 
                            borderColor: isSelected ? '#9333ea' : '#d1d5db', 
                            backgroundColor: isSelected ? '#9333ea' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {isSelected && (
                              <div style={{
                                width: '8px',
                                height: '8px',
                                backgroundColor: 'white',
                                borderRadius: '50%'
                              }} />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '32px', 
                    color: '#9ca3af', 
                    border: '2px dashed #e5e7eb', 
                    borderRadius: '8px' 
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '8px' }}>📱</div>
                    <div style={{ fontSize: '14px' }}>No Instagram Business accounts found</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Connect Instagram Business accounts to your Facebook pages first
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Selection Summary and Actions */}
          <div style={{ borderTop: '1px solid #e5e7eb', padding: '24px', marginTop: '24px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: windowWidth >= 768 ? '1fr 1fr' : '1fr', 
              gap: '24px', 
              marginBottom: '24px' 
            }}>
              {/* Selection Summary */}
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#eff6ff', 
                borderRadius: '6px', 
                border: '1px solid #bfdbfe' 
              }}>
                <h3 style={{ 
                  fontWeight: '500', 
                  color: '#1e40af', 
                  marginBottom: '8px', 
                  fontSize: '14px' 
                }}>
                  Selected Connections{isFeedLevel ? ` for ${feedName || 'Feed'}:` : ':'}
                </h3>
                <div style={{ fontSize: '14px', color: '#1d4ed8' }}>
                  {selectedFacebookPage && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ color: '#16a34a', marginRight: '8px', fontSize: '12px' }}>✅</span>
                      <span>Facebook: {selectedFacebookPage.name}</span>
                    </div>
                  )}
                  {selectedInstagramAccount && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ color: '#16a34a', marginRight: '8px', fontSize: '12px' }}>✅</span>
                      <span>Instagram: @{selectedInstagramAccount.username}</span>
                    </div>
                  )}
                  {!selectedFacebookPage && !selectedInstagramAccount && (
                    <div style={{ color: '#6b7280' }}>No connections selected</div>
                  )}
                </div>
              </div>

              {/* Help Info - Updated for feed context */}
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fefce8', 
                borderRadius: '6px', 
                border: '1px solid #facc15' 
              }}>
                <h3 style={{ 
                  fontWeight: '500', 
                  color: '#a16207', 
                  marginBottom: '8px', 
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ marginRight: '8px' }}>💡</span>
                  How it works:
                </h3>
                <div style={{ fontSize: '14px', color: '#a16207' }}>
                  {isFeedLevel ? (
                    <>
                      <div style={{ marginBottom: '4px' }}>• Each feed can use different Facebook pages</div>
                      <div style={{ marginBottom: '4px' }}>• Instagram connection is optional</div>
                      <div>• Change selections anytime in feed settings</div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: '4px' }}>• Select one Facebook page and one Instagram account</div>
                      <div style={{ marginBottom: '4px' }}>• Instagram connection is optional</div>
                      <div>• Add more connections anytime in settings</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                onClick={handleCancel}
                style={{
                  padding: '8px 24px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d1d5db'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#e5e7eb'}
              >
                Cancel
              </button>
              <button 
                onClick={handleConnect}
                disabled={isSubmitting || (!selectedFacebookPage && !selectedInstagramAccount)}
                style={{
                  padding: '8px 24px',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #9333ea 100%)',
                  color: 'white',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: (!selectedFacebookPage && !selectedInstagramAccount) || isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: (!selectedFacebookPage && !selectedInstagramAccount) || isSubmitting ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                {isSubmitting 
                  ? 'Connecting...' 
                  : `Connect Selected Accounts${isFeedLevel ? ' to Feed' : ''}`
                }
              </button>
            </div>
          </div>
        </div>
        {/* Connection Settings Help Section */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#64748b',
            fontSize: '14px',
            margin: '0 0 8px 0'
          }}>
            Don't see the page or account you are looking for?{' '}
            <button
              onClick={handleCheckConnectionSettings}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0'
              }}
              onMouseEnter={(e) => e.target.style.color = '#2563eb'}
              onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
            >
              Check your connection settings
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FacebookPageSelector;